import { useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getSamePatientOrdersTemplate } from 'apollo/templates/order';
import { Order } from 'models/order';

export type OperationType = 'WARRANTY_ORDER' | 'COPY_ORDER' | '';

type UseOngoingOrderDialogProps = {
    onOpenWarrantyDialog: () => void;
    onOpenCopyDialog: () => void;
};

export const useOngoingOrderDialog = ({ onOpenWarrantyDialog, onOpenCopyDialog }: UseOngoingOrderDialogProps) => {
    const [open, setOpen] = useState(false);
    const [orders, setOrders] = useState<Order[]>([]);
    const [operationType, setOperationType] = useState<OperationType>('');
    const operationTypeRef = useRef<OperationType | null>(null);
    const orderRef = useRef<Order | null>(null);

    const [fetchOrders] = useLazyQuery(getSamePatientOrdersTemplate, {
        onCompleted: ({ samePatientOrders }) => {
            const currentOrder = orderRef.current;
            // Exclude current order
            const filtered = samePatientOrders?.filter((o: Order) => o?._id !== currentOrder?._id);

            setOrders(filtered);
            if (filtered.length > 0) {
                setOpen(true);
            } else {
                continueOperation();
            }
        },
    });

    const close = () => {
        setOpen(false);
        setOrders([]);
    };

    const continueOperation = () => {
        close();
        const type = operationTypeRef.current;
        if (type === 'WARRANTY_ORDER') onOpenWarrantyDialog();
        if (type === 'COPY_ORDER') onOpenCopyDialog();
    };

    const checkOngoingOrder = async (order: Order, type: OperationType) => {
        operationTypeRef.current = type; // immediate update operationType
        setOperationType(type); // for UI
        orderRef.current = order; // store current order
        const pin = order.patient?.personalIdentityNumber || '';
        if (pin.length >= 6) {
            await fetchOrders({ variables: { personalIdentityNumber: pin } });
        } else {
            continueOperation();
        }
    };

    return {
        open,
        orders,
        operationType, // expose for UI
        close,
        continueOperation,
        checkOngoingOrder,
    };
};
